import jsPDF from "jspdf";
import { Occurrence } from "../models/occurrence";
import { OccurrenceStepLabelEnum } from "../enums/occurrenceStep";
import { OccurrenceTypeLabelEnum } from "../enums/occurrenceType";

export const exportOccurrenceToPdf = async (
  occurrenceData: any
): Promise<void> => {
  try {
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);
    let yPosition = margin;
    const lineHeight = 5;
    const sectionSpacing = 10;
    const primaryColor: [number, number, number] = [43, 161, 172];
    const lightGray: [number, number, number] = [240, 240, 240];
    const darkGray: [number, number, number] = [85, 85, 85];
    const successColor: [number, number, number] = [212, 237, 218];
    const successBorder: [number, number, number] = [40, 167, 69];
    const warningColor: [number, number, number] = [255, 243, 205];
    const warningBorder: [number, number, number] = [255, 193, 7];

    // Verifica se há espaço suficiente na página atual, caso contrário adiciona nova página
    const checkAddPage = (neededSpace: number) => {
      if (yPosition + neededSpace > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Formata data para o padrão brasileiro (DD/MM/YYYY HH:mm)
    const formatDate = (date?: Date | string) => {
      if (!date) return "-";
      const parsedDate = typeof date === 'string' ? new Date(date) : date;
      return parsedDate.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    // Helpers para acessar campos do veículo, motorista e concessionária com fallbacks
    const getVehicleField = (field: string) => {
      return occurrenceData[`vehicle${field.charAt(0).toUpperCase() + field.slice(1)}`] || 
             occurrenceData.vehicle?.[field] || null;
    };
    
    const getDriverField = (field: string) => {
      return occurrenceData[`driver${field.charAt(0).toUpperCase() + field.slice(1)}`] || 
             occurrenceData.driver?.[field] || null;
    };
    
    const getDealershipField = (field: string) => {
      return occurrenceData[`dealership${field.charAt(0).toUpperCase() + field.slice(1)}`] || 
             occurrenceData.dealership?.[field] || null;
    };

    const drawSectionHeader = (title: string) => {
      checkAddPage(20);
      
      pdf.setDrawColor(...primaryColor);
      pdf.setLineWidth(0.5);
      const headerHeight = 10;
      pdf.rect(margin, yPosition, contentWidth, headerHeight, 'S');
      
      pdf.setTextColor(...primaryColor);
      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.text(title, margin + 3, yPosition + 7);
      
      yPosition += headerHeight + 3;
      pdf.setTextColor(0, 0, 0);
    };

    const drawField = (label: string, value: string, xOffset: number = 3, width: number = contentWidth / 2, sameLine: boolean = false) => {
      if (!sameLine) {
        checkAddPage(10);
      }
      
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...darkGray);
      const labelWidth = 35;
      pdf.text(label, margin + xOffset, yPosition);
      
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(0, 0, 0);
      const valueText = value || "-";
      const lines = pdf.splitTextToSize(valueText, width - labelWidth - 5);
      pdf.text(lines, margin + xOffset + labelWidth, yPosition);
      
      if (!sameLine) {
        yPosition += lineHeight * Math.max(lines.length, 1);
      }
      
      return lines.length;
    };

    const closeSectionBorder = (startY: number) => {
      const sectionHeight = yPosition - startY;
      pdf.setDrawColor(...primaryColor);
      pdf.setLineWidth(0.5);
      pdf.rect(margin, startY, contentWidth, sectionHeight, 'S');
      yPosition += sectionSpacing;
    };

    // Cabeçalho do documento
    pdf.setFillColor(...primaryColor);
    pdf.rect(0, 0, pageWidth, 25, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    pdf.setFont("helvetica", "bold");
    pdf.text("Relatório de Ocorrência", margin, 12);
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    pdf.text(`Gerado em: ${formatDate(new Date())}`, margin, 19);
    
    yPosition = 35;
    pdf.setTextColor(0, 0, 0);

    // Seção: Informações do Veículo
    drawSectionHeader("Informações do Veículo");
    const vehicleStartY = yPosition;
    
    const chassiLines = drawField("Chassi:", getVehicleField('chassis') || "-", 3, contentWidth / 2, false);
    const placaY = yPosition - (lineHeight * chassiLines);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...darkGray);
    pdf.text("Placa:", margin + contentWidth / 2 + 3, placaY);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);
    const placaLines = pdf.splitTextToSize(getVehicleField('licensePlate') || "-", contentWidth / 2 - 40);
    pdf.text(placaLines, margin + contentWidth / 2 + 38, placaY);
    
    const modelLines = drawField("Modelo:", getVehicleField('model') || "-", 3, contentWidth / 2, false);
    const anoY = yPosition - (lineHeight * modelLines);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...darkGray);
    pdf.text("Ano:", margin + contentWidth / 2 + 3, anoY);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);
    const yearValue = getVehicleField('vehicleYear') || getVehicleField('year');
    pdf.text(String(yearValue || "-"), margin + contentWidth / 2 + 38, anoY);
    
    const typeLines = drawField("Tipo:", getVehicleField('vehicleType') || getVehicleField('type') || "-", 3, contentWidth / 2, false);
    const nameY = yPosition - (lineHeight * typeLines);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...darkGray);
    pdf.text("Nome:", margin + contentWidth / 2 + 3, nameY);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);
    const nameLines = pdf.splitTextToSize(getVehicleField('name') || "-", contentWidth / 2 - 40);
    pdf.text(nameLines, margin + contentWidth / 2 + 38, nameY);
    
    const odometerField = getVehicleField('odometer');
    const odometerValue = odometerField ? `${odometerField} km` : "-";
    const odometerLines = drawField("Odômetro:", odometerValue, 3, contentWidth / 2, false);
    const horimeterY = yPosition - (lineHeight * odometerLines);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...darkGray);
    pdf.text("Horímetro:", margin + contentWidth / 2 + 3, horimeterY);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);
    const horimeterField = getVehicleField('hourMeter') || getVehicleField('hourmeter');
    const horimeterValue = horimeterField ? `${horimeterField} h` : "-";
    pdf.text(horimeterValue, margin + contentWidth / 2 + 38, horimeterY);

    closeSectionBorder(vehicleStartY - 13);

    // Seção: Dados da Ocorrência
    drawSectionHeader("Dados da Ocorrência");
    const occStartY = yPosition;
    
    const tipoLines = drawField("Tipo:", OccurrenceTypeLabelEnum[occurrenceData.occurrenceType as keyof typeof OccurrenceTypeLabelEnum] || "-", 3, contentWidth / 2, false);
    const statusY = yPosition - (lineHeight * tipoLines);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...darkGray);
    pdf.text("Status:", margin + contentWidth / 2 + 3, statusY);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);
    pdf.text(occurrenceData.finished ? "Finalizada" : "Em Andamento", margin + contentWidth / 2 + 38, statusY);
    
    drawField("Criticidade:", occurrenceData.criticality || "-", 3);
    
    const createdLines = drawField("Criação:", formatDate(occurrenceData.createdAt), 3, contentWidth / 2, false);
    const updatedY = yPosition - (lineHeight * createdLines);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...darkGray);
    pdf.text("Atualização:", margin + contentWidth / 2 + 3, updatedY);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);
    const updateLines = pdf.splitTextToSize(formatDate(occurrenceData.updatedAt), contentWidth / 2 - 40);
    pdf.text(updateLines, margin + contentWidth / 2 + 38, updatedY);

    closeSectionBorder(occStartY - 13);

    // Seção: Concessionária
    drawSectionHeader("Concessionária");
    const dealerStartY = yPosition;
    
    const dealerNameLines = drawField("Nome:", getDealershipField('name') || "-", 3, contentWidth / 2, false);
    const dnY = yPosition - (lineHeight * dealerNameLines);
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...darkGray);
      pdf.text("DN:", margin + contentWidth / 2 + 3, dnY);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(0, 0, 0);
      pdf.text(String(getDealershipField('dn') || "-"), margin + contentWidth / 2 + 38, dnY);
    
    const regionalLines = drawField("Regional:", getDealershipField('regional') || "-", 3, contentWidth / 2, false);
    const locationY = yPosition - (lineHeight * regionalLines);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...darkGray);
    pdf.text("Localização:", margin + contentWidth / 2 + 3, locationY);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);
    const locationLines = pdf.splitTextToSize(getDealershipField('local') || getDealershipField('location') || "-", contentWidth / 2 - 40);
    pdf.text(locationLines, margin + contentWidth / 2 + 38, locationY);

    closeSectionBorder(dealerStartY - 13);

    // Seção: Motorista
    drawSectionHeader("Motorista");
    const driverStartY = yPosition;
    
    const driverNameLines = drawField("Nome:", getDriverField('name') || "-", 3, contentWidth / 2, false);
    const cnhY = yPosition - (lineHeight * driverNameLines);
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...darkGray);
      pdf.text("CNH:", margin + contentWidth / 2 + 3, cnhY);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(0, 0, 0);
      pdf.text(String(getDriverField('driverLicenseNumber') || getDriverField('licenseNumber') || "-"), margin + contentWidth / 2 + 38, cnhY);
    
    drawField("Telefone:", getDriverField('phone') || "-", 3);
    drawField("Localização:", getDriverField('checkInDriver') || occurrenceData.manualDriverLocation || "-", 3);

    closeSectionBorder(driverStartY - 13);

    // Seção: Histórico de Etapas
    const steps = occurrenceData.occurrenceSteps || occurrenceData.steps || [];
    if (steps.length > 0) {
      drawSectionHeader("Histórico de Etapas");
      const stepsContainerY = yPosition;
      
      steps
        .sort((a: any, b: any) => (a.latest || 0) - (b.latest || 0))
        .forEach((step: any, index: number) => {
          const stepId = step.stepId || step.step || step.id || step.type;
          let stepLabel = step.name || step.stepName || step.label;
          
          if (!stepLabel && stepId) {
            stepLabel = OccurrenceStepLabelEnum[stepId as keyof typeof OccurrenceStepLabelEnum];
          }
          
          if (!stepLabel) {
            stepLabel = stepId || `Etapa ${index + 1}`;
          }
          
          const isCompleted = step.dtEnd !== null && step.dtEnd !== undefined;
          
          checkAddPage(40);
          
          const boxHeight = 8;
          const boxColor = isCompleted ? successColor : warningColor;
          const borderColor = isCompleted ? successBorder : warningBorder;
          
          pdf.setFillColor(...boxColor);
          pdf.rect(margin + 3, yPosition, contentWidth - 6, boxHeight, 'F');
          
          pdf.setDrawColor(...borderColor);
          pdf.setLineWidth(0.5);
          pdf.rect(margin + 3, yPosition, contentWidth - 6, boxHeight, 'S');
          
          pdf.setFontSize(11);
          pdf.setFont("helvetica", "bold");
          pdf.setTextColor(...borderColor);
          pdf.text(`${index + 1}. ${stepLabel} ${isCompleted ? "✓" : "⏳"}`, margin + 5, yPosition + 5.5);
          
          yPosition += boxHeight + 3;
          pdf.setTextColor(0, 0, 0);
          
          const stepFieldStartY = yPosition;
          
          const startLines = drawField("Início:", formatDate(step.dtStart), 5, contentWidth / 2, false);
          const conclusionY = yPosition - (lineHeight * startLines);
          pdf.setFontSize(9);
          pdf.setFont("helvetica", "bold");
          pdf.setTextColor(...darkGray);
          pdf.text("Conclusão:", margin + contentWidth / 2 + 5, conclusionY);
          pdf.setFont("helvetica", "normal");
          pdf.setTextColor(0, 0, 0);
          const conclusionValue = step.dtEnd ? formatDate(step.dtEnd) : "-";
          const conclusionLines = pdf.splitTextToSize(conclusionValue, contentWidth / 2 - 43);
          pdf.text(conclusionLines, margin + contentWidth / 2 + 40, conclusionY);
          
          if (step.estimatedTime) {
            drawField("Tempo Estimado:", step.estimatedTime, 5, contentWidth - 10);
          }
          
          if (step.report) {
            checkAddPage(25);
            pdf.setFontSize(9);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(...darkGray);
            pdf.text("Relato:", margin + 5, yPosition);
            yPosition += lineHeight;
            
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(0, 0, 0);
            const reportLines = pdf.splitTextToSize(step.report, contentWidth - 12);
            pdf.text(reportLines, margin + 5, yPosition);
            yPosition += lineHeight * reportLines.length;
          }
          
          if (step.observation) {
            checkAddPage(25);
            pdf.setFontSize(9);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(...darkGray);
            pdf.text("Observações:", margin + 5, yPosition);
            yPosition += lineHeight;
            
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(0, 0, 0);
            const obsLines = pdf.splitTextToSize(step.observation, contentWidth - 12);
            pdf.text(obsLines, margin + 5, yPosition);
            yPosition += lineHeight * obsLines.length;
          }
          
          if (step.updatedBy) {
            drawField("Atualizado por:", step.updatedBy, 5, contentWidth - 10);
          }
          
          yPosition += 5;
        });
    }

    // Seção: Módulos/DTC
    if (occurrenceData.modules && occurrenceData.modules.length > 0) {
      drawSectionHeader("Módulos/DTC");
      const modulesStartY = yPosition;
      
      pdf.setFillColor(...lightGray);
      pdf.rect(margin + 3, yPosition, contentWidth - 6, 7, 'F');
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(0, 0, 0);
      pdf.text("Nome", margin + 5, yPosition + 4.5);
      pdf.text("SPN", margin + 80, yPosition + 4.5);
      pdf.text("FMI", margin + 120, yPosition + 4.5);
      yPosition += 9;
      
      occurrenceData.modules.forEach((module: any, index: number) => {
        checkAddPage(8);
        
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        
        const nameLines = pdf.splitTextToSize(String(module.name || "-"), 70);
        pdf.text(nameLines, margin + 5, yPosition);
        pdf.text(String(module.spn || "-"), margin + 80, yPosition);
        pdf.text(String(module.fmi || "-"), margin + 120, yPosition);
        
        yPosition += lineHeight * nameLines.length;
      });
      
      closeSectionBorder(modulesStartY - 13);
    }

    // Seção: Pedidos de Peças
    if (occurrenceData.partOrders && occurrenceData.partOrders.length > 0) {
      drawSectionHeader("Pedidos de Peças");
      const partsStartY = yPosition;
      
      occurrenceData.partOrders.forEach((order: any, orderIndex: number) => {
        checkAddPage(30);
        
        pdf.setFillColor(249, 249, 249);
        const orderBoxStartY = yPosition;
        
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(51, 51, 51);
        pdf.text(`Pedido ${orderIndex + 1}: ${order.number || "-"}`, margin + 5, yPosition + 5);
        yPosition += 8;
        
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "normal");
        pdf.text(`Status: ${String(order.status || "-")}`, margin + 5, yPosition);
        yPosition += lineHeight;
        pdf.text(`Data: ${formatDate(order.date)}`, margin + 5, yPosition);
        yPosition += lineHeight + 2;
        
        if (occurrenceData.partOrderParts && occurrenceData.partOrderParts.length > 0) {
          pdf.setFillColor(...lightGray);
          pdf.rect(margin + 5, yPosition, contentWidth - 10, 6, 'F');
          pdf.setFontSize(8);
          pdf.setFont("helvetica", "bold");
          pdf.text("Part Number", margin + 7, yPosition + 4);
          pdf.text("Quantidade", margin + contentWidth - 35, yPosition + 4);
          yPosition += 8;
          
          occurrenceData.partOrderParts.forEach((part: any) => {
            checkAddPage(7);
            
            const partNumber = typeof part.partNumber === 'string' 
              ? part.partNumber 
              : `${part.partNumber.code} - ${part.partNumber.description}`;
            
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(7);
            const partLines = pdf.splitTextToSize(partNumber, contentWidth - 55);
            pdf.text(partLines, margin + 7, yPosition);
            pdf.text(String(part.quantity || "-"), margin + contentWidth - 35, yPosition);
            
            yPosition += 4 * partLines.length;
          });
        }
        
        const orderBoxHeight = yPosition - orderBoxStartY;
        pdf.setDrawColor(221, 221, 221);
        pdf.setLineWidth(0.3);
        pdf.rect(margin + 3, orderBoxStartY, contentWidth - 6, orderBoxHeight, 'S');
        
        yPosition += 5;
      });
      
      closeSectionBorder(partsStartY - 13);
    }

    // Seção: Avaliações
    if (occurrenceData.reviews && occurrenceData.reviews.length > 0) {
      drawSectionHeader("Avaliações");
      const reviewsStartY = yPosition;
      
      occurrenceData.reviews.forEach((review: any) => {
        checkAddPage(20);
        
        const stepLabel = OccurrenceStepLabelEnum[review.step as keyof typeof OccurrenceStepLabelEnum] || review.step;
        const stars = "⭐".repeat(review.rate || 0);
        
        pdf.setFillColor(249, 249, 249);
        const reviewBoxStartY = yPosition;
        
        pdf.setDrawColor(...primaryColor);
        pdf.setLineWidth(1);
        pdf.line(margin + 3, yPosition, margin + 3, yPosition + 15);
        
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(0, 0, 0);
        pdf.text(`Etapa: ${stepLabel}`, margin + 7, yPosition + 4);
        yPosition += lineHeight;
        
        pdf.setFont("helvetica", "normal");
        pdf.text(`Avaliação: ${stars} (${review.rate || 0}/5)`, margin + 7, yPosition + 4);
        yPosition += lineHeight;
        
        if (review.comment) {
          pdf.setFont("helvetica", "bold");
          pdf.text("Comentário:", margin + 7, yPosition + 4);
          yPosition += lineHeight;
          
          pdf.setFont("helvetica", "normal");
          const commentLines = pdf.splitTextToSize(review.comment, contentWidth - 14);
          pdf.text(commentLines, margin + 7, yPosition);
          yPosition += lineHeight * commentLines.length;
        }
        
        yPosition += 5;
      });
      
      closeSectionBorder(reviewsStartY - 13);
    }

    // Rodapé
    pdf.setFontSize(8);
    pdf.setTextColor(119, 119, 119);
    pdf.text(`Criado em ${formatDate(new Date())}`, pageWidth / 2, pageHeight - 10, { align: "center" });

    const filename = `Ocorrencia_${occurrenceData.vehicleChassis}_${new Date().toISOString().split("T")[0]}.pdf`;
    pdf.save(filename);
    
    console.log("PDF gerado com sucesso!");
  } catch (error) {
    console.error("Erro ao gerar PDF:", error);
    throw error;
  }
};

